import React, { useEffect, useMemo, useRef, useState } from "react";
import { CloseIcon, UserIcon, DoctorInactiveIcon, CalendarWhite, TimeIcon } from 'assets/icons'
import { Constants, convertFrom24HourFormat, Globals } from "utils";
import { Button, DatePicker, ImageSelect, Select, TimePicker } from "components";
import moment from "moment";
import { AppoimentService } from "services";
import { useSelector } from "react-redux";

interface ModalViewAppointmentProps {
  appointment?: any
  onClose: (reload?: boolean) => void
  specialities: any[]
  doctors: any[]
  states: any[]
}

interface Form {
  speciality: string,
  location: string,
  doctor_id: string,
  date: Date | null,
  time: string,
}

const ACTIONS = {
  VIEW: 0,
  EDIT: 1,
}

const INITAL_STATE: Form = {
  speciality: '',
  location: '',
  doctor_id: '',
  date: null,
  time: '',
}

const ModalViewAppointment = ({ appointment, onClose, specialities, doctors, states }: ModalViewAppointmentProps) => {
  const user = useSelector((state: any) => state.user);
  const [isEdit, setIsEdit] = useState(ACTIONS.VIEW);
  const [form, setForm] = useState(INITAL_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [doctorInfo, setDoctorInfo] = useState<any>(null);
  const prevDoctorInfo = useRef<any>(null);



  useEffect(() => {
    const currentDoctorInfo = {
      phone_code: appointment?.doctor?.phone_code?.name,
      phone: appointment?.doctor?.phone,
      email: appointment?.doctor?.email,
      address: appointment?.doctor?.address,
      disabled_week_days: [],
      disabled_days: [],
    }

    if (appointment) {
      const obj = {
        speciality: String(appointment?.doctor?.specialty?.id),
        location: String(appointment?.doctor?.state_id),
        doctor_id: String(appointment?.doctor?.id),
        date: moment(appointment?.date).toDate(),
        time: appointment?.time,
      }

      setForm(obj)
    }

    prevDoctorInfo.current = currentDoctorInfo;
    setDoctorInfo(currentDoctorInfo);
  }, [])


  const handleMonthChange = async (date: any) => {
    const doctor = doctors.find((doctor: any) => doctor.id === +form.doctor_id) || null;
    const res = await AppoimentService.getDoctorCalendar({ doctor_id: +form.doctor_id, date }) as any
    setDoctorInfo({
      phone_code: doctor?.phone_code,
      phone: doctor?.phone,
      email: doctor?.email,
      address: doctor?.address,
      disabled_week_days: res?.disabled_week_days || [],
      disabled_days: res?.disabled_days || [],
    })
  }


  const photo = useMemo(() => {
    return {
      patient: appointment?.user?.photo ? Globals.fromStorage(appointment?.user?.photo) : null,
      doctor: appointment?.doctor?.photo ? Globals.fromStorage(appointment?.doctor?.photo) : null,
    }
  }, [appointment])


  const handleEdit = () => {
    setIsEdit(prev => prev === ACTIONS.VIEW ? ACTIONS.EDIT : ACTIONS.VIEW)
    setDoctorInfo(prevDoctorInfo.current);
  };

  const onChange = (value: any, target: string) => setForm(prev => ({ ...prev, [target]: value }));



  const isFormValid = () => {
    if (!form.speciality) {
      Globals.showError('Debe seleccionar una especialidad');
      return false;
    }

    if (!form.location) {
      Globals.showError('Debe seleccionar una ubicación');
      return false;
    }

    if (!form.doctor_id) {
      Globals.showError('Debe seleccionar un médico');
      return false;
    }

    if (!form.date) {
      Globals.showError('Debe seleccionar una fecha');
      return false;
    }


    return true;
  }

  const onSubmit = async () => {
    if (!isFormValid()) return;
    try {
      setIsLoading(true);
      const data = {
        ...form,
        id: appointment?.id,
        date: moment(form.date).format('YYYY-MM-DD'),
        user_id: user?.id,
      }
      await AppoimentService.update(data)
      Globals.showSuccess('La consulta fue actualizada exitosamente')
      onClose(true);
    } catch (error) {
      const err = error as any;
      Globals.showError(err?.response?.data?.error || 'Ocurrió un error al actualizar la consulta')
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div >
      <header>
        <p className="title">Paciente</p>
        <button type="button" className="close-btn" onClick={() => onClose()}>
          <img src={CloseIcon} alt="Cerrar" />
        </button>
      </header>

      <section className="card card-patient">
        <div className="user">
          <div className="user-photo">
            {
              photo.patient
                ? <img src={photo.patient} alt="Usuario" />
                : <img src={UserIcon} alt="Usuario" className="invert-image" />
            }
          </div>
          <p>{appointment?.user?.full_name}</p>
        </div>

        <div className="user-data">
          <p className="title">C.I.</p>
          <p>{appointment?.user?.document}</p>
        </div>

        <div className="user-data">
          <p className="title">Teléfono</p>
          <p>{appointment?.user?.phone_code?.name}{appointment?.user?.phone}</p>
        </div>

        <div className="user-data">
          <p className="title">Correo</p>
          <p>{appointment?.user?.email}</p>
        </div>
      </section>


      <p className="title">Médico</p>
      <section className="card card-doctor">
        {
          isEdit ? (
            <DoctorSelect
              onChange={onChange}
              form={form}
              specialities={specialities}
              doctors={doctors}
              states={states}
            />
          ) : (
            <div className="user">
              <div className="user-photo">
                {
                  photo.doctor
                    ? <img src={photo.doctor} alt="Usuario" />
                    : <img src={UserIcon} alt="Usuario" className="invert-image" />
                }
              </div>
              <p className="doctor-name bold">{appointment?.doctor?.full_name}</p>
              <p className="doctor-speciality bold"> - {appointment?.doctor?.specialty?.name}</p>
            </div>
          )
        }

        <div className="doctor-info">
          <div>
            <span className="bold">Teléfono: </span>
            <span>{doctorInfo?.phone}</span>
          </div>

          <div>
            <span className="bold">Correo: </span>
            <span>{doctorInfo?.email}</span>
          </div>
        </div>

        <div className="address">
          <p>{doctorInfo?.address}</p>
        </div>
      </section>

      <section className="appoiment-info">
        <div className="appoiment-price">
          <div className="icon">
            <img src={DoctorInactiveIcon} alt="Médico" />
          </div>
          <div>
            <p className="title">{appointment?.type?.name}</p>
            <p className="price">
              <span className="bold">
                {Globals.formatMiles(appointment?.service?.price)}
              </span>
              {!appointment?.plan_id && (
                <span> / {Globals.formatMiles(appointment?.payment?.amount_local, true, 'Bs')}</span>
              )}
            </p>
          </div>
        </div>

        <div className="appoiment-date">
          <div>
            <p className="bold label-font-family label">
              <span><img src={CalendarWhite} alt="calendar" className="invert-image icon-label" /></span>
              Fecha
            </p>
          </div>
          {
            isEdit ? (
              <DatePicker
                placeholder="Fecha"
                minDate={moment().toDate()}
                maxDate={moment().add(1, 'year').toDate()}
                onChange={(v: string) => onChange(v, 'date')}
                value={form.date}
                disabledWeekDays={doctorInfo.disabled_week_days}
                disabledDays={doctorInfo?.disabled_days}
                onMonthChange={handleMonthChange}
                hideOtherMonths
                onOpen={() => {
                  const date = moment().format('YYYY-MM-DD');
                  handleMonthChange(date);
                }}
              />
            ) : (
              <p>{moment(appointment?.date).format('DD-MM-YYYY')}</p>
            )
          }
        </div>
      </section>


      <div className="btn-container">
        {appointment?.status === Constants.APPOINTMENT.STATUS.PENDING && (
          !isEdit ? (
            <Button className="btn-edit" onClick={handleEdit}>
              Editar
            </Button>
          ) : (
            <>
              <Button
                className="btn-save"
                onClick={onSubmit}
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                Guardar
              </Button>
              <Button className="btn-cancel bold" onClick={handleEdit}>
                Cancelar
              </Button>
            </>
          )
        )}
      </div>

    </div>
  )
}

interface Props {
  onChange: (target: any, value: any) => void
  form: Form,
  specialities: any[]
  doctors: any[]
  states: any[]
}


const DoctorSelect = ({ onChange, form, specialities, doctors, states }: Props) => {

  const doctorOptions = useMemo(() => {
    return doctors
      .filter((doctor: any) => doctor.specialty_id === +form.speciality && doctor.state_id === +form.location)
      .map((doctor: any) => ({
        id: doctor.id,
        name: doctor.full_name,
        image: doctor.photo ? Globals.fromStorage(doctor.photo) : null
      }))
  }, [doctors, form.speciality, form.location])


  const onChangeState = (value: string) => {
    onChange(value, 'location');
    onChange('', 'doctor_id')
    onChange(null, 'date')
  }

  return (
    <div className="doctor-edit">
      <Select
        value={form.speciality}
        name="speciality"
        label="Especialidad"
        options={specialities}
        disabled
      />
      <Select
        value={form.location}
        name="location"
        label="Ubicación"
        options={states}
        onChange={(e: any) => onChangeState(e.target.value)}
      />
      <div className="doctor-select">
        <ImageSelect
          label="Médico"
          options={doctorOptions}
          value={form.doctor_id}
          onChange={(selected) => onChange(String(selected.id), 'doctor_id')}
        />
      </div>
    </div>
  )
}









export default ModalViewAppointment